/**
 * Sort + date-window controls shared by the four list surfaces:
 * beneficiaries and sessions, each inside a campaign and standalone.
 *
 * Requested 2026-09-29 ("sort by session created / ended", "filter who I
 * contacted or saw today or on a date"). The query-string shape these
 * emit is parsed server-side by `apps/list_params.py`:
 *
 *   ?sort=<key>            leading "-" for descending
 *   ?<prefix>_on=today | yesterday | YYYY-MM-DD
 *   ?<prefix>_from=YYYY-MM-DD & ?<prefix>_to=YYYY-MM-DD
 *
 * Dates go over the wire as plain YYYY-MM-DD, never as an instant. The
 * server resolves the day in the CAMPAIGN's timezone (then the org's),
 * because an operator asking for "today" means their working day, not
 * the browser's UTC offset. Sending a timestamp would quietly move the
 * boundary for anyone travelling.
 */
import { useId } from "react";
import { useTranslation } from "react-i18next";

import {
  type DateWindow,
  type SortKey,
  emptyWindow,
  todayISO,
  windowIsSet,
} from "./listParams";

interface SortProps {
  value: string;
  onChange: (next: string) => void;
  /** Offered keys, in display order. */
  options: SortKey[];
  label?: string;
}

const SORT_LABEL: Record<SortKey, string> = {
  created: "lists.sort.created",
  contacted: "lists.sort.contacted",
  session_created: "lists.sort.sessionCreated",
  session_ended: "lists.sort.sessionEnded",
  session_started: "lists.sort.sessionStarted",
  started: "lists.sort.sessionStarted",
  ended: "lists.sort.sessionEnded",
  scheduled: "lists.sort.scheduled",
};

export function SortSelect({ value, onChange, options, label }: SortProps) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="lsd-field">
      <label htmlFor={id}>{label ?? t("lists.sortBy")}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="list-sort"
      >
        {options.map((key) => (
          /*
           * Newest-first is the useful default for every one of these
           * columns, so descending is the plain option and ascending is
           * the explicit one rather than offering a separate direction
           * toggle that is wrong by default half the time.
           */
          <optgroup key={key} label={t(SORT_LABEL[key])}>
            <option value={`-${key}`}>{t("lists.dir.newest")}</option>
            <option value={key}>{t("lists.dir.oldest")}</option>
          </optgroup>
        ))}
      </select>
    </div>
  );
}

interface WindowProps {
  value: DateWindow;
  onChange: (next: DateWindow) => void;
  label: string;
  /** Rendered under the control when the field can be blank for
   *  reasons that are not "it never happened". */
  hint?: string;
}

export function DateWindowPicker({
  value,
  onChange,
  label,
  hint,
}: WindowProps) {
  const { t } = useTranslation();
  const id = useId();
  const mode = value.on
    ? value.on === "today" || value.on === "yesterday"
      ? value.on
      : "on"
    : value.from || value.to
      ? "range"
      : "";

  function pick(next: string) {
    if (next === "") onChange(emptyWindow);
    else if (next === "today" || next === "yesterday") onChange({ on: next });
    else if (next === "on") onChange({ on: todayISO() });
    else onChange({ from: todayISO(), to: todayISO() });
  }

  return (
    <div className="lsd-field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={mode}
        onChange={(e) => pick(e.target.value)}
        data-testid="list-date-mode"
      >
        <option value="">{t("lists.date.any")}</option>
        <option value="today">{t("lists.date.today")}</option>
        <option value="yesterday">{t("lists.date.yesterday")}</option>
        <option value="on">{t("lists.date.specific")}</option>
        <option value="range">{t("lists.date.range")}</option>
      </select>

      {mode === "on" && (
        <input
          type="date"
          aria-label={t("lists.date.specific")}
          value={value.on ?? ""}
          onChange={(e) => onChange({ on: e.target.value })}
          data-testid="list-date-on"
        />
      )}

      {mode === "range" && (
        <>
          <input
            type="date"
            aria-label={t("lists.date.from")}
            value={value.from ?? ""}
            onChange={(e) => onChange({ ...value, on: undefined, from: e.target.value })}
            data-testid="list-date-from"
          />
          <input
            type="date"
            aria-label={t("lists.date.to")}
            value={value.to ?? ""}
            onChange={(e) => onChange({ ...value, on: undefined, to: e.target.value })}
            data-testid="list-date-to"
          />
        </>
      )}

      {hint && windowIsSet(value) && <p className="lsd-hint">{hint}</p>}
    </div>
  );
}
